import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import FormInputText from '@app/component/form/input/text/FormInputText';
import CountryAutocompleteField from '@app/component/form/input/autocomplete/CountryAutocompleteField';
import ProfileInput from '@app/model/profile/input/ProfileInput';
import Profile from '@app/model/profile/Profile';
import FormButton from '@app/component/form/button/FormButton';
import { useTranslation } from '@app/component/translation/Translation';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import useSiteState from '@app/state/SiteState';
import { shallow } from 'zustand/shallow';
import SectionHeader from '@app/component/static/SectionHeader';
import { useMutation, useQuery } from '@tanstack/react-query';
import ProfileService from '@app/model/profile/service/ProfileService';
import FormPlaceholder from '@app/component/placeholder/FormPlaceholder';

const ProfileSettingsFragment = React.memo(() => {

    const {data, isSuccess, isLoading} = useQuery({
        queryFn: ProfileService.findProfile,
        queryKey: ['profile']
    });

    if(isLoading) return <FormPlaceholder/>

    else return isSuccess ? <ProfileSettingsForm profile={data}/> : <></>

});

const ProfileSettingsForm: React.FunctionComponent<{ profile: Profile }> = ({profile}) => {

    const [defaultValues, setDefaultValues] = useState<ProfileInput>({
        name: profile.name,
        email: profile.email,
        countryId: profile.company.country.id,
    });

    const form = useForm<ProfileInput>({
        defaultValues: defaultValues
    });

    const setPrincipal = useSiteState(state => state.setPrincipal, shallow);

    const toaster = useToaster();
    const {t} = useTranslation();


    const saveProfileMutation = useMutation({
        mutationFn: ProfileService.saveProfile,
        onSuccess: (data, variables) => {
            toaster.success('Save Profile', "Profile saved successfully");
            setPrincipal(data);
            setDefaultValues({
                name: variables.name,
                email: variables.email,
                countryId: variables.countryId
            })
        },
        onError: (error: string) => toaster.error('Save Profile', t(error))
    })

    const onSubmit = (data: ProfileInput) => {
        saveProfileMutation.mutate({
            name: data.name,
            email: data.email,
            countryId: data.countryId,
        })
    }

    return <form autoComplete="chrome-off">
        <div className="mt-8">
            <SectionHeader>Details</SectionHeader>
            <div>
                <div className="flex flex-col lg:flex-row">
                    <div className="flex-grow space-y-6">
                        <div className="grid grid-cols-12 gap-y-6 sm:gap-6">
                            <div className="col-span-12 sm:col-span-6">
                                <FormInputText name='name' form={form} label='Name' isRequired/>
                            </div>

                            <div className="col-span-12 sm:col-span-6">
                                <FormInputText name='email' form={form} label='Email' disabled/>
                            </div>
                        </div>

                        <div>
                            <CountryAutocompleteField name="countryId" form={form} label="Country"
                                                      placeholder="Please select country" isRequired/>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div className="mt-4 flex justify-end gap-x-3 py-4">
            <FormButton state="white" onClick={() => form.reset(defaultValues)}>
                Reset
            </FormButton>
            <FormButton state="primary" onClick={form.handleSubmit(onSubmit)}>
                Save
            </FormButton>
        </div>
    </form>
}

export default ProfileSettingsFragment;